import React, { useRef, useState, useCallback } from "react";
import api from "../../lib/api";
import { Image as ImageIcon, Upload, Trash2, Loader2, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";

/**
 * Shared UI for uploading / previewing / removing the user's business logo.
 * Used both in the Knowledge Base page and inline in the PostCraft chat header.
 *
 * Props:
 *   logoUrl:    string | null — current logo data URL (from /auth/me).
 *   onChange:   (nextUrl: string | null) => void — parent syncs local state.
 *   compact:    boolean — if true, renders a small pill (used in chat header).
 */
export default function BusinessLogo({ logoUrl, onChange, compact = false }) {
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);

  const handleFile = useCallback(
    async (file) => {
      if (!file) return;
      if (!file.type.startsWith("image/")) {
        toast.error("Please choose an image file");
        return;
      }
      if (file.size > 4 * 1024 * 1024) {
        toast.error("Logo must be under 4 MB");
        return;
      }
      setBusy(true);
      try {
        const dataUrl = await new Promise((resolve, reject) => {
          const r = new FileReader();
          r.onload = () => resolve(r.result);
          r.onerror = reject;
          r.readAsDataURL(file);
        });
        const res = await api.put("/me/business-logo", { image_base64: dataUrl });
        onChange?.(res.data.business_logo_url || null);
        toast.success("Business logo saved");
      } catch (e) {
        toast.error(e?.response?.data?.detail || "Logo upload failed");
      } finally {
        setBusy(false);
        if (fileRef.current) fileRef.current.value = "";
      }
    },
    [onChange]
  );

  const handleRemove = useCallback(async () => {
    setBusy(true);
    try {
      const res = await api.delete("/me/business-logo");
      onChange?.(res.data.business_logo_url || null);
      toast.success("Logo removed");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to remove logo");
    } finally {
      setBusy(false);
    }
  }, [onChange]);

  if (compact) {
    return (
      <div
        data-testid="business-logo-pill"
        className="inline-flex items-center gap-2 px-2.5 py-1.5 rounded-full border border-zinc-800 bg-zinc-950/60 text-xs"
      >
        {logoUrl ? (
          <>
            <img
              src={logoUrl}
              alt="Business logo"
              className="w-5 h-5 rounded-sm object-cover shrink-0"
            />
            <span className="text-emerald-300 hidden sm:inline">Logo attached</span>
            <button
              type="button"
              data-testid="logo-pill-replace"
              onClick={() => fileRef.current?.click()}
              disabled={busy}
              className="text-zinc-400 hover:text-white transition-colors disabled:opacity-40"
              title="Replace logo"
            >
              <Upload className="w-3.5 h-3.5" />
            </button>
            <button
              type="button"
              data-testid="logo-pill-remove"
              onClick={handleRemove}
              disabled={busy}
              className="text-zinc-500 hover:text-rose-300 transition-colors disabled:opacity-40"
              title="Remove logo"
            >
              <Trash2 className="w-3.5 h-3.5" />
            </button>
          </>
        ) : (
          <>
            <ImageIcon className="w-3.5 h-3.5 text-zinc-500" />
            <button
              type="button"
              data-testid="logo-pill-add"
              onClick={() => fileRef.current?.click()}
              disabled={busy}
              className="text-amber-300 hover:text-amber-200 transition-colors disabled:opacity-40"
            >
              {busy ? "Uploading…" : "Add business logo"}
            </button>
          </>
        )}
        <input
          ref={fileRef}
          type="file"
          accept="image/*"
          className="hidden"
          onChange={(e) => handleFile(e.target.files?.[0])}
          data-testid="logo-pill-input"
        />
      </div>
    );
  }

  // Full card (used in KnowledgePage)
  return (
    <div
      data-testid="business-logo-card"
      className="rounded-2xl border border-zinc-800 bg-zinc-950/40 p-5 mb-6"
    >
      <div className="flex items-start gap-4">
        <div className="w-20 h-20 rounded-xl border border-zinc-800 bg-zinc-900/60 flex items-center justify-center shrink-0 overflow-hidden">
          {logoUrl ? (
            <img
              src={logoUrl}
              alt="Business logo"
              className="w-full h-full object-contain"
              data-testid="business-logo-preview"
            />
          ) : (
            <ImageIcon className="w-8 h-8 text-zinc-600" />
          )}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <h3 className="font-display text-lg tracking-tight font-bold text-white">
              Business logo
            </h3>
            {logoUrl && (
              <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.18em] text-emerald-300 border border-emerald-500/30 bg-emerald-500/5 px-2 py-0.5 rounded-full">
                <CheckCircle2 className="w-3 h-3" /> Attached
              </span>
            )}
          </div>
          <p className="text-sm text-zinc-400 leading-relaxed max-w-2xl">
            Upload your business logo once — every branded post PostCraft
            generates for you will include it as an exact replica (no color,
            shape, or text changes). PNG with transparent background works
            best. Under 4 MB.
          </p>
          <div className="mt-3 flex items-center gap-2">
            <button
              type="button"
              data-testid="business-logo-upload-btn"
              onClick={() => fileRef.current?.click()}
              disabled={busy}
              className="inline-flex items-center gap-2 px-3 py-2 rounded-lg text-sm border border-emerald-500/30 text-emerald-300 hover:bg-emerald-500/10 transition-colors disabled:opacity-40"
            >
              {busy ? (
                <Loader2 className="w-3.5 h-3.5 animate-spin" />
              ) : (
                <Upload className="w-3.5 h-3.5" />
              )}
              {logoUrl ? "Replace logo" : "Upload logo"}
            </button>
            {logoUrl && (
              <button
                type="button"
                data-testid="business-logo-remove-btn"
                onClick={handleRemove}
                disabled={busy}
                className="inline-flex items-center gap-2 px-3 py-2 rounded-lg text-sm border border-rose-500/30 text-rose-300 hover:bg-rose-500/10 transition-colors disabled:opacity-40"
              >
                <Trash2 className="w-3.5 h-3.5" /> Remove
              </button>
            )}
            <input
              ref={fileRef}
              type="file"
              accept="image/*"
              className="hidden"
              onChange={(e) => handleFile(e.target.files?.[0])}
              data-testid="business-logo-file-input"
            />
          </div>
        </div>
      </div>
    </div>
  );
}
